import { createSavedViewsController } from '@/features/saved-views/controller';
import { mergeSavedColumns } from '@/features/saved-views/columns';
import { createSavedViewsHooks, type SavedViewsHooks } from '@/features/saved-views/hooks';
import { TRADES_SCOPE, type SavedViewsAdapter } from '@/features/saved-views/types';
import {
  useTradesColumnStore,
  defaultColumns,
  DEFAULT_PINNED_LEFT,
  DEFAULT_PINNED_RIGHT,
  type ColumnConfig,
} from '@/stores/trades/trades-column-store';
import {
  useTradesFilterStore,
  backfillLocalTradeFilters,
  defaultLocalFilters,
} from '@/stores/trades/trades-filter-store';
import { useTradesDataStore } from '@/stores/trades/trades-data-store';
import type { TradesSavedViewPayload, TradesSavedViewColumn } from './types';

// INVARIANT: every field read by `capture()` MUST be persisted by its owning
// store's `partialize`. Otherwise F5 clears the field in-memory while the
// snapshot (rehydrated from the server payload) still holds the old value —
// and `computeIsDirty()` will return true for a reload the user never
// triggered. Currently covered: column-store (columns, pinnedLeft/Right —
// 'trades-columns-storage'), filter-store (localTradesFilters —
// 'trades-filters-storage', persisted since LR-671), data-store (tradesSorting
// — 'trades-data-store'). The active counterparty TAB (views-store) is
// deliberately NOT captured: a saved view is columns / pins / filters / sorting
// only, independent of the tab slice (LR-671 decision).
function capture(): TradesSavedViewPayload {
  const colState = useTradesColumnStore.getState();
  const filterState = useTradesFilterStore.getState();
  const dataState = useTradesDataStore.getState();

  return {
    columns: colState.columns.map((c: ColumnConfig, i: number): TradesSavedViewColumn => ({
      id: c.id,
      visible: c.visible,
      order: i,
    })),
    pinnedLeft: [...colState.pinnedLeft],
    pinnedRight: [...colState.pinnedRight],
    filters: { ...filterState.localTradesFilters },
    sorting: dataState.tradesSorting.map((s) => ({ ...s })),
  };
}

const knownColumnIds = new Set(defaultColumns.map((c) => c.id));

/** Drops pins of columns the schema no longer has, as the column store's rehydrate does. */
const keepKnownPins = (pins: string[]): string[] => pins.filter((id) => knownColumnIds.has(id));

/**
 * Backfills filter fields added AFTER a view was saved (schema drift), so the
 * payload's `filters` always carries the full LocalTradeFilters key set — the
 * raw `set` in apply() bypasses the persist merge, so a legacy view would
 * strand a new field as `undefined` and the sidebar's `filters.<field>.length`
 * would TypeError. A payload without `filters` passes through — the consumer
 * decides between reset-to-defaults and keep-as-is.
 */
export function withBackfilledFilters<
  T extends { filters?: Partial<TradesSavedViewPayload['filters']> },
>(payload: T): T & { filters?: TradesSavedViewPayload['filters'] } {
  return payload?.filters
    ? { ...payload, filters: backfillLocalTradeFilters(payload.filters) }
    : (payload as T & { filters?: undefined });
}

function apply(payload: TradesSavedViewPayload | Record<string, never>): void {
  const p = (payload ?? {}) as Partial<TradesSavedViewPayload>;
  const colStore = useTradesColumnStore.getState();
  const filterStore = useTradesFilterStore.getState();
  const dataStore = useTradesDataStore.getState();

  const payloadColumns = p.columns ?? [];
  if (payloadColumns.length > 0) {
    colStore.setColumns(mergeSavedColumns(colStore.columns, payloadColumns, defaultColumns));
  } else {
    // Empty payload (system view with {}, or post-delete fallback): the merge
    // branch above would keep the previous view's visibility/order untouched
    // because there are no saved entries to override with. Reset to factory
    // defaults instead — otherwise switching back to the system view after
    // using a customised view leaves the customised columns in place.
    colStore.resetColumns();
  }
  useTradesColumnStore.setState({
    pinnedLeft: keepKnownPins(p.pinnedLeft ?? DEFAULT_PINNED_LEFT),
    pinnedRight: keepKnownPins(p.pinnedRight ?? DEFAULT_PINNED_RIGHT),
  });

  // If payload has no filters (e.g. system default view with `{}`), reset
  // to factory defaults rather than crash.
  if (p.filters) {
    filterStore.setLocalTradesFilters(withBackfilledFilters(p).filters!);
  } else {
    filterStore.clearLocalTradesFilters();
  }

  dataStore.setTradesSorting(p.sorting ?? []);
}

/** The F5 skip-apply snapshot — same contract as the inventory adapter's toSavedSnapshot. */
export function toSavedSnapshot(
  payload: TradesSavedViewPayload | Record<string, never>,
): TradesSavedViewPayload {
  const p = (payload ?? {}) as Partial<TradesSavedViewPayload>;
  const columns = p.columns?.length
    ? mergeSavedColumns(useTradesColumnStore.getState().columns, p.columns, defaultColumns)
    : defaultColumns;
  return {
    columns: columns.map((c, i) => ({ id: c.id, visible: c.visible, order: i })),
    pinnedLeft: keepKnownPins(p.pinnedLeft ?? DEFAULT_PINNED_LEFT),
    pinnedRight: keepKnownPins(p.pinnedRight ?? DEFAULT_PINNED_RIGHT),
    filters: p.filters ? withBackfilledFilters(p).filters! : defaultLocalFilters,
    sorting: p.sorting ?? [],
  };
}

function subscribeToChanges(listener: () => void): () => void {
  // One zustand subscribe per store. Any relevant-field change triggers the
  // listener; the controller then recomputes dirty via capture()+isEqual.
  let prevColumns = useTradesColumnStore.getState().columns;
  let prevPinnedLeft = useTradesColumnStore.getState().pinnedLeft;
  let prevPinnedRight = useTradesColumnStore.getState().pinnedRight;
  const unsubCol = useTradesColumnStore.subscribe((state) => {
    if (
      state.columns !== prevColumns ||
      state.pinnedLeft !== prevPinnedLeft ||
      state.pinnedRight !== prevPinnedRight
    ) {
      prevColumns = state.columns;
      prevPinnedLeft = state.pinnedLeft;
      prevPinnedRight = state.pinnedRight;
      listener();
    }
  });

  let prevFilters = useTradesFilterStore.getState().localTradesFilters;
  const unsubFilter = useTradesFilterStore.subscribe((state) => {
    if (state.localTradesFilters !== prevFilters) {
      prevFilters = state.localTradesFilters;
      listener();
    }
  });

  let prevSorting = useTradesDataStore.getState().tradesSorting;
  const unsubData = useTradesDataStore.subscribe((state) => {
    if (state.tradesSorting !== prevSorting) {
      prevSorting = state.tradesSorting;
      listener();
    }
  });

  return () => {
    unsubCol();
    unsubFilter();
    unsubData();
  };
}

export const tradesAdapter: SavedViewsAdapter<TradesSavedViewPayload> = {
  scope: TRADES_SCOPE,
  capture,
  apply,
  subscribeToChanges,
};

export const tradesSavedViewsController = createSavedViewsController(tradesAdapter);

const tradesSavedViewsHooks = createSavedViewsHooks<TradesSavedViewPayload>(TRADES_SCOPE);
export function getTradesSavedViewsHooks(): SavedViewsHooks<TradesSavedViewPayload> {
  return tradesSavedViewsHooks;
}
